import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import { createUseStyles, useTheme, Badge, Button, Icon, Text, Markdown } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';

import styles from '../styles/AddOnsCard';
const useStyles = createUseStyles(styles, 'AddOnsCard');

const AddOnsCard = ({
    title,
    icon,
    disabled,
    info,
    badge,
    content,
    figure,
    onHover,
    itemCode,
    linkCallback,
}) => {
    const { t } = useWeb5Context();
    const classes = useStyles();
    const theme = useTheme();

    const rootProps = useMemo(
        () => ({
            className: classNames(classes.root, { [classes.hasInfo]: !!info }),
            onMouseEnter: () => onHover && onHover(itemCode),
        }),
        [classes.hasInfo, classes.root, info, itemCode, onHover],
    );

    const getIcon = useMemo(() => {
        switch (true) {
            case typeof icon === 'string':
                const iconProps = {
                    name: icon,
                    className: classes.icon,
                    color: disabled
                        ? theme.colors.utility.textDisabled
                        : theme.colors.actionMajor[500],
                    size: 'large',
                };
                return <Icon {...iconProps} />;
            case typeof icon === 'object' && !!icon?.customIcon:
                return <span className={classes.icon}>{icon.customIcon}</span>;
            default:
                return null;
        }
    }, [classes.icon, disabled, icon, theme]);

    const getBadge = useMemo(() => {
        if (!badge) return null;
        const { text, ...badgeProps } = badge;
        return (
            <Badge className={classes.badge} {...badgeProps}>
                {text}
            </Badge>
        );
    }, [badge, classes.badge]);

    const hoverContent = useMemo(() => {
        if (!info || typeof info !== 'object') return null;
        const { texts, cta } = info;
        return (
            <div className={classes.hoverContent}>
                {texts && (
                    <Markdown
                        className={classes.hoverContentMarkdown}
                        content={texts}
                        overrides={{
                            a: { target: '_blank' },
                            p: { color: theme.colors.textLight.secondary },
                        }}
                        linkCallback={linkCallback}
                    />
                )}
                {!!cta?.length && (
                    <div className={classes.hoverContentCTA}>
                        {cta.map((button, index) => (
                            <Button type={button.type} onClick={button.onClick} key={index}>
                                {button.text}
                            </Button>
                        ))}
                    </div>
                )}
            </div>
        );
    }, [
        classes.hoverContent,
        classes.hoverContentCTA,
        classes.hoverContentMarkdown,
        info,
        linkCallback,
        theme,
    ]);

    const Img = useMemo(() => figure?.img, [figure]);

    return (
        <div {...rootProps}>
            <div className={classes.header}>
                {getIcon}
                <Text type="h6" bold color={theme.colors.textLight.primary}>
                    {title}
                </Text>
                {getBadge}
            </div>
            <div className={classes.main}>
                {content && (
                    <div className={classes.content}>
                        {(content.price || content.noPriceInfo) && (
                            <div className={classes.contentHeadline}>
                                {content.price && (
                                    <Text
                                        type="caption"
                                        color={theme.colors.textLight.primary}
                                        className={classes.price}
                                    >
                                        {t(
                                            'label_addon_price_per_month',
                                            [content.price],
                                            (text) => (
                                                <Text
                                                    type="h6"
                                                    color={theme.colors.textLight.primary}
                                                >
                                                    {text}
                                                </Text>
                                            ),
                                        )}
                                    </Text>
                                )}
                                {content.noPriceInfo && (
                                    <Markdown
                                        content={content.noPriceInfo}
                                        overrides={{
                                            p: {
                                                type: 'caption',
                                                color: theme.colors.textLight.primary,
                                            },
                                        }}
                                    />
                                )}
                            </div>
                        )}
                        {content.description && (
                            <Text type="caption" color={theme.colors.textLight.secondary}>
                                {content.description}
                            </Text>
                        )}
                    </div>
                )}
                {figure && (
                    <figure className={classes.figure}>
                        {Img && <Img />}
                        {figure.caption && (
                            <figcaption>
                                <Text type="badges">{figure.caption}</Text>
                            </figcaption>
                        )}
                    </figure>
                )}
            </div>
            {hoverContent}
        </div>
    );
};

export default memo(AddOnsCard);
